import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../../common/httpclient';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../../component/table/DefaultTable';
import FilterListIcon from '@mui/icons-material/FilterList';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddIcon from '@mui/icons-material/Add';
import usePermissions from '../../../hooks/usePermissions';
import DialogForm from '../../../component/dialog/DialogForm';
import { standaloneFieldSx } from '../../../common/fieldStyles';

export default function PermissionsList() {
    const theme = useTheme();
    const navigate = useNavigate();
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [displayNameFilter, setDisplayNameFilter] = useState('');
    const [groupPermissionFilter, setGroupPermissionFilter] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const { hasPermission } = usePermissions();
    const [openModal, setOpenModal] = useState(false);
    const [creating, setCreating] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        display_name: '',
        group_permission: ''
    });

    const fetchPermissions = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            
            if (displayNameFilter) {
                params.append('display_name', displayNameFilter);
            }
            
            if (groupPermissionFilter) {
                params.append('group_permission', groupPermissionFilter);
            }

            const url = `/admin/permissions${params.toString() ? `?${params.toString()}` : ''}`;
            const response = await httpClient.get(url);
            
            if (response.status === 200 && response.data) {
                setPermissions(Array.isArray(response.data) ? response.data : []);
                setFilteredData(Array.isArray(response.data) ? response.data : []);
            } else {
                setPermissions([]);
                setFilteredData([]);
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setPermissions([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPermissions();
    }, [displayNameFilter, groupPermissionFilter]);

    const handleDisplayNameChange = (value) => {
        setDisplayNameFilter(value);
    };

    const handleGroupPermissionChange = (value) => {
        setGroupPermissionFilter(value);
    };

    const handleOpenModal = () => {
        setFormData({
            name: '',
            display_name: '',
            group_permission: ''
        });
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setFormData({
            name: '',
            display_name: '',
            group_permission: ''
        });
    };

    const handleCreatePermission = async (event) => {
        event.preventDefault();
        
        if (!formData.name || !formData.display_name || !formData.group_permission) {
            return;
        }

        try {
            setCreating(true);
            const payload = {
                name: formData.name,
                display_name: formData.display_name,
                group_permission: formData.group_permission
            };

            const response = await httpClient.post('/admin/permissions', payload);

            if (response.status === 200 || response.status === 201) {
                // Preencher o filtro com a nova permissão criada
                setDisplayNameFilter(formData.display_name);
                setGroupPermissionFilter(formData.group_permission);
                
                // Fechar o modal
                handleCloseModal();
                
                // Recarregar a lista (o useEffect já vai fazer isso quando os filtros mudarem)
            } else {
                console.error('Error creating permission:', response);
                alert('Erro ao criar permissão. Por favor, tente novamente.');
            }
        } catch (error) {
            console.error('Error creating permission:', error);
            alert('Erro ao criar permissão. Por favor, tente novamente.');
        } finally {
            setCreating(false);
        }
    };

    const columns = [
        {
            name: 'ID',
            path: 'id'
        },
        {
            name: 'Nome',
            path: 'name'
        },
        {
            name: 'Nome de Exibição',
            path: 'display_name'
        },
        {
            name: 'Grupo de Permissão',
            path: 'group_permission'
        }
    ];

    const actions = [
       {
            icon: <EditNoteIcon />,
            props: {
                onClick: (row) => {
                    navigate(`/admin/permissions/edit/${row.id}`);
                },
                tooltip: 'Editar',
                style: { cursor: 'pointer' }
            }
        }
    ];

    return (
        <PageScaffold links={[]} titlePageTarget={'Permissões'}>
            {/* Seção de Filtros Moderna */}
            <Card
                sx={{
                    m: 0,
                    borderRadius: 3,
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 20px rgba(0,0,0,0.3)'
                        : '0 4px 20px rgba(0,0,0,0.08)',
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                        : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                    border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.1)'
                        : '1px solid rgba(0,0,0,0.05)'
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    {/* Header dos Filtros */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 3,
                        gap: 1
                    }}>
                        <FilterListIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: '1.25rem'
                        }}>
                            Filtros de Permissões
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {/* Campo de Busca por Nome de Exibição */}
                        <Box sx={{ flex: '1 1 300px', minWidth: { xs: 0, sm: '300px' } }}>
                            <TextField
                                id="display-name-filter-input"
                                placeholder="Buscar por nome de exibição..."
                                value={displayNameFilter}
                                onChange={(e) => handleDisplayNameChange(e.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: 'primary.main' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'transparent',
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : 'rgba(0,0,0,0.1)',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme.palette.text.primary,
                                    },
                                }}
                            />
                        </Box>

                        {/* Campo de Busca por Grupo de Permissão */}
                        <Box sx={{ flex: '1 1 300px', minWidth: { xs: 0, sm: '300px' } }}>
                            <TextField
                                id="group-permission-filter-input"
                                placeholder="Buscar por grupo de permissão..."
                                value={groupPermissionFilter}
                                onChange={(e) => handleGroupPermissionChange(e.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <GroupIcon sx={{ color: 'primary.main' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'transparent',
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : 'rgba(0,0,0,0.1)',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme.palette.text.primary,
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Ações Rápidas */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 3,
                        pt: 2,
                        borderTop: theme.palette.mode === 'dark'
                            ? '1px solid rgba(255,255,255,0.08)'
                            : '1px solid rgba(0,0,0,0.08)'
                    }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleOpenModal}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: theme.palette.mode === 'dark'
                                        ? '0 4px 12px rgba(0,0,0,0.3)'
                                        : '0 4px 12px rgba(0,0,0,0.15)',
                                    background: theme.palette.mode === 'dark'
                                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    '&:hover': {
                                        background: theme.palette.mode === 'dark'
                                            ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                                            : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                        boxShadow: theme.palette.mode === 'dark'
                                            ? '0 6px 16px rgba(0,0,0,0.4)'
                                            : '0 6px 16px rgba(0,0,0,0.2)',
                                        transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                Nova Permissão
                            </Button>
                            {displayNameFilter && (
                                <Chip
                                    label={`Nome de Exibição: "${displayNameFilter}"`}
                                    onDelete={() => handleDisplayNameChange('')}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : undefined,
                                        color: theme.palette.mode === 'dark'
                                            ? 'primary.light'
                                            : undefined,
                                    }}
                                />
                            )}
                            {groupPermissionFilter && (
                                <Chip
                                    label={`Grupo: "${groupPermissionFilter}"`}
                                    onDelete={() => handleGroupPermissionChange('')}
                                    color="secondary"
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : undefined,
                                        color: theme.palette.mode === 'dark'
                                            ? 'secondary.light'
                                            : undefined,
                                    }}
                                />
                            )}
                            <Chip
                                label={`${filteredData.length} permissão(ões) encontrada(s)`}
                                color="info"
                                variant="outlined"
                                size="small"
                                sx={{
                                    borderColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.2)'
                                        : undefined,
                                    color: theme.palette.mode === 'dark'
                                        ? 'info.light'
                                        : undefined,
                                }}
                            />
                        </Stack>
                    </Box>
                </CardContent>
            </Card>

            <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden', mt: 2 }}>
                <DefaultTable
                    colunms={columns}
                    data={filteredData}
                    iconsActions={actions}
                    loading={loading}
                    sortableFields={['name', 'display_name', 'group_permission']}
                    defaultSort={{ field: 'display_name', direction: 'asc' }}
                />
            </Box>

            {/* Modal para criar nova permissão */}
            <DialogForm
                open={openModal}
                onClose={handleCloseModal}
                onSubmit={handleCreatePermission}
                title="Nova Permissão"
                subtitle="Preencha os dados da nova permissão"
                iconType="info"
                confirmButtonText="Criar"
                cancelButtonText="Cancelar"
                loading={creating}
                loadingText="Criando..."
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                    <TextField
                        label="Nome"
                        fullWidth
                        variant="outlined"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="ex: example_permission"
                        required
                        sx={standaloneFieldSx}
                    />

                    <TextField
                        label="Nome de Exibição"
                        fullWidth
                        variant="outlined"
                        value={formData.display_name}
                        onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                        placeholder="ex: Exemplo de Permissão"
                        required
                        sx={standaloneFieldSx}
                    />

                    <TextField
                        label="Grupo de Permissão"
                        fullWidth
                        variant="outlined"
                        value={formData.group_permission}
                        onChange={(e) => setFormData({ ...formData, group_permission: e.target.value })}
                        placeholder="ex: example_group"
                        required
                        sx={standaloneFieldSx}
                    />
                </Box>
            </DialogForm>
        </PageScaffold>
    );
}

